import React, { useEffect, useState } from 'react';
import { Box, Container, Grid, Card, CardContent, TextField, InputAdornment, Button, Typography, FormControl, Select, MenuItem, SelectChangeEvent, InputLabel } from '@mui/material';
import { styled } from '@mui/system';
import { getDictionary, getField } from '@/app/utils/translation/translation';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { getCountryCode, sendMessage } from '@/app/utils/contactus/contactus';
import { useCountries } from '@/app/contexts/CountriesContext';
import { useRouter } from 'next/navigation';
import { getSvg } from '@/app/utils/svgUtils';





const CustomTextField = styled(TextField)(({ theme }) => ({
    '& input:-webkit-autofill': {
        WebkitBoxShadow: '0 0 0 3000px #f3e1ff inset',
        WebkitTextFillColor: 'var(--text-primary)',
        transition: 'background-color 5000s ease-in-out 0s',
    },
    '& .MuiInputBase-input': {
        color: 'var(--text-primary)',
    },
    '& .MuiInputLabel-root': {
        color: 'var(--text-primary)',
    },
    '& .MuiOutlinedInput-root': {
        '& fieldset': {
            borderColor: 'var(--border-color)',
        },
        '&:hover fieldset': {
            borderColor: 'var(--border-color-hover)',
        },
    },
}));

const ContactFormCard = styled(Card)<{ isMobile?: boolean }>(({ isMobile }) => ({
    maxWidth: isMobile ? '100%' : '400px',
    padding: '2rem',
    backgroundColor: 'var(--section-bg)',
    marginLeft: "auto"
}));
interface Country {
    countryName: string;
    code: string;
    phone: string;

}
const initialState = { companyName: " ", firstName: " ", lastName: " ", message: " ", email: " ", phone: " " }
export default function ContactUs({ isMobile, language, translations, contactusRef, text, showTitle = true, customImage, sideImage }: { isMobile: boolean, language: any, translations: any, contactusRef: any, text?: React.ReactNode, showTitle?: boolean, customImage?: string, sideImage?: string }) {
    const dictionary = getDictionary(translations, 'contact', language);
    const [countryCode, setCountryCode] = useState('');
    const [formData, setFormData] = useState(initialState);
    const isArabic = language === 'arabic';
    const { countries } = useCountries(); // Get countries from context
    const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const { id, value } = event.target;
        setFormData(prevFormData => ({
            ...prevFormData,
            [id]: value,
        }));
    };
    const handleCountryCodeChange = (event: SelectChangeEvent<string>) => {
        setCountryCode(event.target.value as string);
    };


    useEffect(() => {
        const loadCountryInfo = async () => {
            const token = process.env.NEXT_PUBLIC_IPINFO_TOKEN || '';
            const { code } = await getCountryCode(countries, token);
            if (code) {
                setCountryCode(code);
            }
        };

        if (countries.length > 0) {
            loadCountryInfo();
        }
    }, [countries]);


    const router = useRouter();
    const langCode = language === 'arabic' ? 'ar' : language === 'french' ? 'fr' : 'en';

    const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        const { success, message } = await sendMessage(formData);
        if (success) {
            toast.success('Message sent successfully!');
            setFormData(initialState);
            router.push(langCode === 'en' ? `/thankyou` : `/${langCode}/thankyou`);
        } else {
            toast.error(message);
        }

    };


    const isFormValid = () => {
        return Object.values(formData).every(field => field.trim() !== '') && countryCode !== '';

    };

    return (
        <>

            <div 
                className="section-light-purple-bg"
                style={{
                    height: isMobile ? "100%" : '300px',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    width: '100%', 
                    paddingTop: "5px",
                    margin: isMobile ? "3rem 0 0 0" : "14rem 0",
                    paddingBottom: "1rem"

                }}
            >
                <Container maxWidth="lg" ref={contactusRef} id="contact-us">
                    <Grid
                        container
                        spacing={2}
                        direction="row"
                        alignItems="center"
                        justifyContent="center"
                    >
                        <Grid item xs={12} md={5}>
                            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: "center", justifyContent: "center", position: 'relative', height: '100%', maxWidth: isMobile ? '100%' : '400px', margin: '0 auto' }}>
                                {showTitle && (
                                    <div style={{ position: 'relative', zIndex: 0, width: isMobile ? '100%' : 'auto' }}>
                                        <Typography 
                                            variant="h4" 
                                            component="h4"
                                            sx={{ 
                                                color: "var(--accent-primary)",
                                                fontWeight: "bold",
                                                fontFamily: "century Gothic",
                                                textAlign: isMobile ? 'left' : 'center',
                                            }}
                                        >
                                            {getField(dictionary, 'get-a', language)}
                                        </Typography>
                                        {!isMobile && (
                                            <div>
                                                {getSvg('ourproductsarrow', { darkPurple: 'var(--accent-primary)' }, { width: "100px", height: "50px" }, { position: 'absolute', minWidth: 100, minHeight: 50, right: 0 })}
                                            </div>
                                        )}
                                    </div>
                                )}
                                {text && (
                                    <div style={{ textAlign: isArabic ? 'right' : 'left', color: 'var(--text-primary)', width: '100%', padding: '0 1rem' }}>
                                        {text}
                                    </div>
                                )}
                                {sideImage && (
                                    <div style={{ marginTop: "20px" }}>
                                        <img src={sideImage} width={150} height={100} alt="Contact illustration" style={{ minWidth: 100, minHeight: 60 }} />
                                    </div>
                                )}
                                {!isMobile && showTitle && customImage && !sideImage && (
                                    <div style={{ position: 'absolute', bottom: '-50px', left: '50%', transform: 'translateX(-50%)', zIndex: 1 }}>
                                        <img 
                                            src={customImage} 
                                            alt='up-arrow' 
                                        />
                                    </div>
                                )}
                            </div>

                        </Grid>
                        <Grid item xs={12} sm={12} md={6} sx={{ marginTop: isMobile ? "1rem" : "0" }} justifyContent={"center"} >
                            <ContactFormCard isMobile={isMobile}>
                                <CardContent>
                                    <Box
                                        component="form"
                                        sx={{
                                            '& .MuiTextField-root': { m: 1.5, width: '100%' },
                                        }}
                                        noValidate
                                        autoComplete="off"
                                        onSubmit={handleSubmit}
                                    >
                                        <CustomTextField
                                            required
                                            id="companyName"
                                            label={getField(dictionary, 'company-name', language)}
                                            variant="outlined"
                                            value={formData.companyName}
                                            onChange={handleChange}
                                            size="small"
                                            dir={isArabic ? 'rtl' : 'ltr'}
                                            InputLabelProps={{
                                                sx: isArabic
                                                    ? {
                                                        right: 27,
                                                        left: 'auto',
                                                        transformOrigin: 'top right',
                                                        textAlign: 'right',
                                                    }
                                                    : {},
                                            }}
                                            sx={{
                                                '& .MuiOutlinedInput-root': {
                                                    direction: isArabic ? 'rtl' : 'ltr',
                                                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                                        borderColor: 'var(--accent-primary)',
                                                    },
                                                },

                                                '& .MuiInputLabel-root.Mui-focused': {
                                                    color: 'var(--accent-primary)',
                                                },
                                                '& label.MuiInputLabel-shrink': isArabic
                                                    ? {
                                                        right: 28,
                                                        left: 'auto',
                                                    }
                                                    : {},
                                                '& .MuiInputLabel-root': isArabic
                                                    ? {
                                                        right: 22,
                                                        left: 'auto',
                                                    }
                                                    : {},
                                                '& legend': {
                                                    textAlign: isArabic ? 'right' : 'left',
                                                },
                                            }}

                                        />
                                        <CustomTextField
                                            required
                                            id="firstName"
                                            label={getField(dictionary, 'firstName', language)}
                                            variant="outlined"
                                            value={formData.firstName}
                                            onChange={handleChange}
                                            size="small"
                                            dir={isArabic ? 'rtl' : 'ltr'}
                                            InputLabelProps={{
                                                sx: isArabic
                                                    ? {
                                                        right: 27,
                                                        left: 'auto',
                                                        transformOrigin: 'top right',
                                                        textAlign: 'right',
                                                    }
                                                    : {},
                                            }}
                                            sx={{
                                                '& .MuiOutlinedInput-root': {
                                                    direction: isArabic ? 'rtl' : 'ltr',
                                                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                                        borderColor: 'var(--accent-primary)',
                                                    },
                                                },

                                                '& .MuiInputLabel-root.Mui-focused': {
                                                    color: 'var(--accent-primary)',
                                                },
                                                '& label.MuiInputLabel-shrink': isArabic
                                                    ? {
                                                        right: 28,
                                                        left: 'auto',
                                                    }
                                                    : {},
                                                '& .MuiInputLabel-root': isArabic
                                                    ? {
                                                        right: 22,
                                                        left: 'auto',
                                                    }
                                                    : {},
                                                '& legend': {
                                                    textAlign: isArabic ? 'right' : 'left',
                                                },
                                            }}
                                        />
                                        <CustomTextField
                                            required
                                            id="lastName"
                                            label={getField(dictionary, 'lastName', language)}
                                            variant="outlined"
                                            value={formData.lastName}
                                            onChange={handleChange}
                                            size="small"
                                            dir={isArabic ? 'rtl' : 'ltr'}
                                            InputLabelProps={{
                                                sx: isArabic
                                                    ? {
                                                        right: 27,
                                                        left: 'auto',
                                                        transformOrigin: 'top right',
                                                        textAlign: 'right',
                                                    }
                                                    : {},
                                            }}
                                            sx={{
                                                '& .MuiOutlinedInput-root': {
                                                    direction: isArabic ? 'rtl' : 'ltr',
                                                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                                        borderColor: 'var(--accent-primary)',
                                                    },
                                                },

                                                '& .MuiInputLabel-root.Mui-focused': {
                                                    color: 'var(--accent-primary)',
                                                },
                                                '& label.MuiInputLabel-shrink': isArabic
                                                    ? {
                                                        right: 28,
                                                        left: 'auto',
                                                    }
                                                    : {},
                                                '& .MuiInputLabel-root': isArabic
                                                    ? {
                                                        right: 22,
                                                        left: 'auto',
                                                    }
                                                    : {},
                                                '& legend': {
                                                    textAlign: isArabic ? 'right' : 'left',
                                                },
                                            }}
                                        />
                                        <FormControl fullWidth variant="outlined" >
                                            <CustomTextField
                                                id="phone"
                                                label={getField(dictionary, 'phone-number', language)}
                                                variant="outlined"
                                                value={formData.phone}
                                                onChange={handleChange}
                                                size="small"
                                                dir={isArabic ? 'rtl' : 'ltr'}
                                                InputLabelProps={{
                                                    sx: isArabic
                                                        ? {
                                                            right: 27,
                                                            left: 'auto',
                                                            transformOrigin: 'top right',
                                                            textAlign: 'right',
                                                        }
                                                        : {},
                                                }}
                                                sx={{
                                                    '& .MuiOutlinedInput-root': {
                                                        direction: isArabic ? 'rtl' : 'ltr',
                                                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                                            borderColor: 'var(--accent-primary)',
                                                        },
                                                    },

                                                    '& .MuiInputLabel-root.Mui-focused': {
                                                        color: 'var(--accent-primary)',
                                                    },
                                                    '& label.MuiInputLabel-shrink': isArabic
                                                        ? {
                                                            right: 28,
                                                            left: 'auto',
                                                        }
                                                        : {},
                                                    '& .MuiInputLabel-root': isArabic
                                                        ? {
                                                            right: 22,
                                                            left: 'auto',
                                                        }
                                                        : {},
                                                    '& legend': {
                                                        textAlign: isArabic ? 'right' : 'left',
                                                    },
                                                }}
                                                InputProps={{
                                                    startAdornment: (
                                                        <InputAdornment position="start">
                                                            <FormControl variant="outlined" size="small" >
                                                                <Select
                                                                    value={countryCode}
                                                                    onChange={handleCountryCodeChange}
                                                                    displayEmpty
                                                                    inputProps={{ 'aria-label': 'Country Code' }}
                                                                    sx={{
                                                                        border: 'none',
                                                                        '& .MuiSelect-select': {
                                                                            padding: '8px 16px',
                                                                            color: 'var(--text-primary)',
                                                                        },
                                                                        '& .MuiSelect-icon': {
                                                                            color: 'var(--text-primary)',
                                                                        },
                                                                        '& .MuiOutlinedInput-notchedOutline': {
                                                                            border: 'none',
                                                                        },
                                                                    }}
                                                                    MenuProps={{
                                                                        disableScrollLock: true,
                                                                        PaperProps: {
                                                                            sx: {
                                                                                backgroundColor: 'var(--section-bg)',
                                                                                '& .MuiMenuItem-root': {
                                                                                    color: 'var(--text-primary)',
                                                                                    '&:hover': {
                                                                                        backgroundColor: 'var(--section-bg-hover)',
                                                                                    },
                                                                                },
                                                                            },
                                                                        },
                                                                    }}
                                                                >
                                                                    {countries.map((country) => (
                                                                        <MenuItem key={country.code} value={country.code}> {/* Use country.code here */}
                                                                            <div style={{ display: 'flex', alignItems: 'center', color: 'var(--text-primary)' }}>
                                                                                <img
                                                                                    loading="lazy"
                                                                                    width={20}
                                                                                    height={14}
                                                                                    srcSet={`https://flagcdn.com/w40/${country.code.toLowerCase()}.png 2x`}
                                                                                    src={`https://flagcdn.com/w20/${country.code.toLowerCase()}.png`}
                                                                                    alt={`Flag of ${country.countryName}`}
                                                                                    style={{ marginRight: 8 }}
                                                                                />
                                                                                {country.code} ({country.phone})
                                                                            </div>
                                                                        </MenuItem>
                                                                    ))}
                                                                </Select>
                                                            </FormControl>
                                                        </InputAdornment>
                                                    ),
                                                }}
                                            />
                                        </FormControl>
                                        <CustomTextField
                                            id="email"
                                            label={getField(dictionary, 'email', language)}
                                            variant="outlined"
                                            type="email"
                                            value={formData.email}
                                            required
                                            onChange={handleChange}
                                            size="small"
                                            dir={isArabic ? 'rtl' : 'ltr'}
                                            InputLabelProps={{
                                                sx: isArabic
                                                    ? {
                                                        right: 27,
                                                        left: 'auto',
                                                        transformOrigin: 'top right',
                                                        textAlign: 'right',
                                                    }
                                                    : {},
                                            }}
                                            sx={{
                                                '& .MuiOutlinedInput-root': {
                                                    direction: isArabic ? 'rtl' : 'ltr',
                                                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                                        borderColor: 'var(--accent-primary)',
                                                    },
                                                },

                                                '& .MuiInputLabel-root.Mui-focused': {
                                                    color: 'var(--accent-primary)',
                                                },
                                                '& label.MuiInputLabel-shrink': isArabic
                                                    ? {
                                                        right: 28,
                                                        left: 'auto',
                                                    }
                                                    : {},
                                                '& .MuiInputLabel-root': isArabic
                                                    ? {
                                                        right: 22,
                                                        left: 'auto',
                                                    }
                                                    : {},
                                                '& legend': {
                                                    textAlign: isArabic ? 'right' : 'left',
                                                },
                                            }}
                                        />

                                        <CustomTextField
                                            id="message"
                                            label={getField(dictionary, 'message', language)}
                                            multiline
                                            rows={4}
                                            size="small"
                                            variant="outlined"
                                            value={formData.message}
                                            onChange={handleChange}
                                            required
                                            dir={isArabic ? 'rtl' : 'ltr'}
                                            InputLabelProps={{
                                                sx: isArabic
                                                    ? {
                                                        right: 27,
                                                        left: 'auto',
                                                        transformOrigin: 'top right',
                                                        textAlign: 'right',
                                                    }
                                                    : {},
                                            }}
                                            sx={{
                                                '& .MuiOutlinedInput-root': {
                                                    direction: isArabic ? 'rtl' : 'ltr',
                                                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                                        borderColor: 'var(--accent-primary)',
                                                    },
                                                },

                                                '& .MuiInputLabel-root.Mui-focused': {
                                                    color: 'var(--accent-primary)',
                                                },
                                                '& label.MuiInputLabel-shrink': isArabic
                                                    ? {
                                                        right: 28,
                                                        left: 'auto',
                                                    }
                                                    : {},
                                                '& .MuiInputLabel-root': isArabic
                                                    ? {
                                                        right: 22,
                                                        left: 'auto',
                                                    }
                                                    : {},
                                                '& legend': {
                                                    textAlign: isArabic ? 'right' : 'left',
                                                },
                                            }}
                                        />
                                        <Button
                                            type="submit"
                                            variant="contained"
                                            disabled={!isFormValid()}
                                            sx={{
                                                mt: 2, 
                                                width: '100%', 
                                                backgroundColor: "var(--accent-secondary)", 
                                                color: "black",
                                                '&:hover:not(:disabled)': {
                                                    backgroundColor: 'var(--accent-primary)',
                                                    color: 'white',
                                                },
                                                '&.Mui-disabled': {
                                                    backgroundColor: 'var(--accent-secondary)',
                                                    color: 'black',
                                                    opacity: 1,
                                                },
                                            }}
                                        >
                                            {getField(dictionary, 'submit', language)}
                                        </Button>
                                    </Box>
                                </CardContent>
                            </ContactFormCard>
                        </Grid>
                    </Grid>
                </Container>
            </div>
            <ToastContainer />
        </>
    );
}
